import React, { useState, useEffect, useRef } from 'react';
import * as d3 from 'd3';

import Button from '../../../components/ui/Button';

const ClusteringVisualization = ({ 
  selectedNarratives, 
  coordinationThreshold,
  onNarrativeSelect,
  onClusterAnalyze 
}) => {
  const svgRef = useRef(null);
  const [selectedCluster, setSelectedCluster] = useState(null);
  const [viewMode, setViewMode] = useState('network');

  // Mock clustering data with coordination patterns
  const clusterData = {
    nodes: [
      {
        id: 'n1',
        title: 'Energy Crisis Manipulation',
        category: 'Economic',
        strength: 85,
        countries: ['DE', 'PL', 'CZ'],
        claims: 12,
        coordination: 0.78,
        x: 200,
        y: 150
      },
      {
        id: 'n2',
        title: 'Immigration Fearmongering',
        category: 'Social',
        strength: 67,
        countries: ['FR', 'IT', 'HU'],
        claims: 8,
        coordination: 0.65,
        x: 400,
        y: 200
      },
      {
        id: 'n3',
        title: 'EU Sovereignty Undermining',
        category: 'Political',
        strength: 92,
        countries: ['PL', 'HU', 'SK'],
        claims: 15,
        coordination: 0.89,
        x: 300,
        y: 100
      },
      {
        id: 'n4',
        title: 'Climate Change Denial',
        category: 'Environmental',
        strength: 38,
        countries: ['ES', 'PT'],
        claims: 5,
        coordination: 0.42,
        x: 150,
        y: 300
      },
      {
        id: 'n5',
        title: 'Vaccine Misinformation',
        category: 'Health',
        strength: 54,
        countries: ['IT', 'RO', 'BG'],
        claims: 9,
        coordination: 0.58,
        x: 450,
        y: 300
      }
    ],
    links: [
      { source: 'n1', target: 'n3', strength: 0.85, type: 'coordination' },
      { source: 'n2', target: 'n5', strength: 0.62, type: 'thematic' },
      { source: 'n3', target: 'n2', strength: 0.45, type: 'geographic' },
      { source: 'n1', target: 'n4', strength: 0.38, type: 'temporal' },
      { source: 'n4', target: 'n5', strength: 0.52, type: 'coordination' }
    ]
  };

  const categoryColors = {
    'Economic': '#DC2626',
    'Social': '#D97706',
    'Political': '#1E3A8A',
    'Environmental': '#059669',
    'Health': '#7C3AED'
  };

  const linkTypes = {
    'coordination': { color: '#DC2626', dash: 'none' },
    'thematic': { color: '#D97706', dash: '5,5' },
    'geographic': { color: '#1E3A8A', dash: '3,3' },
    'temporal': { color: '#059669', dash: '8,2' }
  };

  useEffect(() => {
    if (!svgRef?.current) return;

    const svg = d3?.select(svgRef?.current);
    svg?.selectAll("*")?.remove();

    const width = 600;
    const height = 400;
    
    svg?.attr("width", width)?.attr("height", height);

    if (viewMode === 'network') {
      // Draw links
      clusterData?.links?.forEach(link => {
        const sourceNode = clusterData?.nodes?.find(n => n?.id === link?.source);
        const targetNode = clusterData?.nodes?.find(n => n?.id === link?.target);
        
        if (sourceNode && targetNode && link?.strength >= coordinationThreshold) {
          const linkStyle = linkTypes?.[link?.type];
          
          svg?.append("line")?.attr("x1", sourceNode?.x)?.attr("y1", sourceNode?.y)?.attr("x2", targetNode?.x)?.attr("y2", targetNode?.y)?.attr("stroke", linkStyle?.color)?.attr("stroke-width", Math.max(1, link?.strength * 4))?.attr("stroke-dasharray", linkStyle?.dash)?.attr("opacity", 0.7);
        }
      });

      // Draw nodes
      clusterData?.nodes?.forEach(node => {
        const nodeGroup = svg?.append("g")?.style("cursor", "pointer")?.on("click", () => {
            setSelectedCluster(node);
            onNarrativeSelect(node?.id);
          });

        // Node circle
        nodeGroup?.append("circle")?.attr("cx", node?.x)?.attr("cy", node?.y)?.attr("r", Math.max(15, (node?.strength / 100) * 30))?.attr("fill", categoryColors?.[node?.category])?.attr("opacity", selectedNarratives?.includes(node?.id) ? 1 : 0.7)?.attr("stroke", selectedCluster?.id === node?.id ? "#1E293B" : "#FFFFFF")?.attr("stroke-width", selectedCluster?.id === node?.id ? 3 : 2);

        // Node label
        nodeGroup?.append("text")?.attr("x", node?.x)?.attr("y", node?.y - Math.max(15, (node?.strength / 100) * 30) - 8)?.attr("text-anchor", "middle")?.attr("font-size", "11px")?.attr("font-weight", "600")?.attr("fill", "#1E293B")?.text(node?.title?.length > 20 ? node?.title?.substring(0, 20) + '...' : node?.title);

        // Strength indicator
        nodeGroup?.append("text")?.attr("x", node?.x)?.attr("y", node?.y + 4)?.attr("text-anchor", "middle")?.attr("font-size", "10px")?.attr("font-weight", "700")?.attr("fill", "#FFFFFF")?.text(node?.strength);
      });
    } else {
      // Hierarchical clustering view
      const hierarchy = d3?.hierarchy({
        name: "Narratives",
        children: Object.entries(
          clusterData?.nodes?.reduce((acc, node) => {
            if (!acc?.[node?.category]) acc[node.category] = [];
            acc?.[node?.category]?.push(node);
            return acc;
          }, {})
        )?.map(([category, nodes]) => ({
          name: category,
          children: nodes
        }))
      });

      const treeLayout = d3?.tree()?.size([width - 100, height - 100]);
      treeLayout(hierarchy);

      // Draw links
      svg?.selectAll(".link")?.data(hierarchy?.links())?.enter()?.append("path")?.attr("class", "link")?.attr("d", d3?.linkHorizontal()?.x(d => d?.y + 50)?.y(d => d?.x + 50))?.attr("fill", "none")?.attr("stroke", "#64748B")?.attr("stroke-width", 2);

      // Draw nodes
      const nodeGroups = svg?.selectAll(".node")?.data(hierarchy?.descendants())?.enter()?.append("g")?.attr("class", "node")?.attr("transform", d => `translate(${d?.y + 50},${d?.x + 50})`);

      nodeGroups?.append("circle")?.attr("r", d => d?.children ? 8 : 12)?.attr("fill", d => d?.data?.category ? categoryColors?.[d?.data?.category] : "#64748B")?.attr("stroke", "#FFFFFF")?.attr("stroke-width", 2);

      nodeGroups?.append("text")?.attr("dy", ".35em")?.attr("x", d => d?.children ? -15 : 15)?.attr("text-anchor", d => d?.children ? "end" : "start")?.attr("font-size", "11px")?.attr("font-weight", "500")?.attr("fill", "#1E293B")?.text(d => d?.data?.name || d?.data?.title);
    }

  }, [viewMode, coordinationThreshold, selectedNarratives, selectedCluster]);

  const viewModes = [
    { id: 'network', label: 'Network View', icon: 'GitBranch' },
    { id: 'hierarchy', label: 'Hierarchy View', icon: 'TreePine' }
  ];

  return (
    <div className="bg-card rounded-lg border border-border shadow-soft overflow-hidden">
      {/* Visualization Controls */}
      <div className="flex items-center justify-between p-4 border-b border-border bg-muted/30">
        <div className="flex items-center space-x-4">
          <h3 className="text-lg font-semibold text-foreground">Narrative Clustering</h3>
          <div className="flex items-center space-x-2">
            {viewModes?.map((mode) => (
              <Button
                key={mode?.id}
                variant={viewMode === mode?.id ? "default" : "outline"}
                size="sm"
                iconName={mode?.icon}
                iconPosition="left"
                onClick={() => setViewMode(mode?.id)}
              >
                {mode?.label}
              </Button>
            ))}
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-muted-foreground">Coordination Threshold:</span>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={coordinationThreshold}
              onChange={(e) => onClusterAnalyze(parseFloat(e?.target?.value))}
              className="w-20 h-2 bg-muted rounded-lg appearance-none cursor-pointer"
            />
            <span className="text-sm font-medium text-foreground w-8">
              {(coordinationThreshold * 100)?.toFixed(0)}%
            </span>
          </div>

          <Button
            variant="outline"
            size="sm"
            iconName="RefreshCw"
            iconPosition="left"
          >
            Refresh
          </Button>
        </div>
      </div>
      {/* Clustering Visualization */}
      <div className="relative bg-slate-50 min-h-[400px] flex items-center justify-center">
        <svg ref={svgRef} className="w-full h-full" />

        {/* Category Legend */}
        <div className="absolute top-4 left-4 bg-card/95 backdrop-blur-sm rounded-lg border border-border p-3 shadow-soft">
          <h4 className="text-sm font-semibold text-foreground mb-2">Categories</h4>
          <div className="space-y-1">
            {Object.entries(categoryColors)?.map(([category, color]) => (
              <div key={category} className="flex items-center space-x-2">
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: color }}
                ></div>
                <span className="text-xs text-muted-foreground">{category}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Link Types Legend */}
        {viewMode === 'network' && (
          <div className="absolute top-4 right-4 bg-card/95 backdrop-blur-sm rounded-lg border border-border p-3 shadow-soft">
            <h4 className="text-sm font-semibold text-foreground mb-2">Connection Types</h4>
            <div className="space-y-1">
              {Object.entries(linkTypes)?.map(([type, style]) => (
                <div key={type} className="flex items-center space-x-2">
                  <svg width="20" height="2">
                    <line
                      x1="0"
                      y1="1"
                      x2="20"
                      y2="1"
                      stroke={style?.color}
                      strokeWidth="2"
                      strokeDasharray={style?.dash}
                    />
                  </svg>
                  <span className="text-xs text-muted-foreground capitalize">{type}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      {/* Selected Cluster Details */}
      {selectedCluster && (
        <div className="p-4 border-t border-border bg-muted/30">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h4 className="text-lg font-semibold text-foreground mb-2">
                {selectedCluster?.title}
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                <div>
                  <span className="text-xs text-muted-foreground">Strength</span>
                  <div className="text-sm font-semibold text-foreground">{selectedCluster?.strength}/100</div>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Countries</span>
                  <div className="text-sm font-semibold text-foreground">{selectedCluster?.countries?.length}</div>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Claims</span>
                  <div className="text-sm font-semibold text-foreground">{selectedCluster?.claims}</div>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Coordination</span>
                  <div className="text-sm font-semibold text-foreground">
                    {(selectedCluster?.coordination * 100)?.toFixed(0)}%
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-muted-foreground">Affected Countries:</span>
                <div className="flex space-x-1">
                  {selectedCluster?.countries?.map(country => (
                    <span 
                      key={country}
                      className="px-2 py-1 bg-primary/10 text-primary text-xs font-medium rounded"
                    >
                      {country}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                iconName="ExternalLink"
                iconPosition="left"
                onClick={() => onNarrativeSelect(selectedCluster?.id)}
              >
                Analyze
              </Button>
              <Button
                variant="ghost"
                size="icon"
                iconName="X"
                onClick={() => setSelectedCluster(null)}
              />
            </div>
          </div>
        </div>
      )}
      {/* Statistics Summary */}
      <div className="flex items-center justify-between p-4 border-t border-border bg-muted/30">
        <div className="flex items-center space-x-6 text-sm text-muted-foreground">
          <span>{clusterData?.nodes?.length} narratives</span>
          <span>{clusterData?.links?.filter(l => l?.strength >= coordinationThreshold)?.length} connections</span>
          <span>{Object.keys(categoryColors)?.length} categories</span>
        </div>
        
        <div className="text-sm text-muted-foreground">
          Last analysis: {new Date()?.toLocaleTimeString()}
        </div>
      </div>
    </div>
  );
};

export default ClusteringVisualization;